import React from "react";
import type { ApolloClient } from "@apollo/client";
import {
  map,
  each,
  filter,
  includes,
  orderBy,
  isEmpty,
  size,
  startCase,
} from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { getReports } from "actions/adminActions";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { Form, Card, Row, Col } from "react-bootstrap";
import { simulateLinkClickNewTab } from "utils/dom";
import { getComplianceReportLink } from "services/adminService";
import { toast } from "react-toastify";
import { FiFolder } from "react-icons/fi";

import Button from "components/Button";
import moment from "moment";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import IconMessageCard from "components/IconMessageCard";
import IconHeader from "components/IconHeader";
import type { RootState } from "types/state";

/** A single compliance-report row rendered by IconTable. */
interface ReportRow {
  date: string;
}

function buildDateSelectOptions(reportDates: string[]) {
  const seenMonthYears: Record<string, boolean> = {};
  const options: React.ReactElement[] = [];
  const sortedReportDates = orderBy(reportDates, (date) => date, "desc");
  each(sortedReportDates, (date) => {
    const monthYearString = moment(date).format("YYYY-MM");
    if (!seenMonthYears[monthYearString]) {
      options.push(
        <option value={monthYearString} key={monthYearString}>
          {monthYearString}
        </option>
      );
      seenMonthYears[monthYearString] = true;
    }
  });
  return options;
}

const isFetching = createLoadingSelector(adminConstants.GET_COMPLIANCE_REPORTS);

const mapStateToProps = (state: RootState) => {
  return {
    // admin slice types complianceReports as Record<string, unknown>; this page
    // consumes it as report-type -> array of ISO date strings — cast at boundary
    reports: state.admin.complianceReports as Record<string, string[]>,
    isFetching: isFetching(state),
  };
};

const mapDispatchToProps = {
  getReports,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  selectedReportType: string;
  selectedYearMonth: string;
}

class AdminReports extends React.PureComponent<Props, State> {
  constructor() {
    // original calls super() with no props (this.props unused here); preserve
    // verbatim rather than forwarding props, which would alter the super call
    // @ts-expect-error React.Component requires props arg; preserving no-arg super()
    super();

    this.state = {
      selectedReportType: "",
      selectedYearMonth: "",
    };
  }

  componentDidMount() {
    void this.props.getReports(this.props.client as ApolloClient<object>);
  }

  _downloadCSV = (date: string) => {
    getComplianceReportLink(
      this.props.client as ApolloClient<object>,
      this.state.selectedReportType,
      date
    )
      .then((result) => {
        // codegen types getAdminReportLink as nullable; original JS accessed
        // result.uri unguarded (would throw on null). Non-null assertion is
        // compile-time only and preserves that exact runtime behavior.
        simulateLinkClickNewTab(result!.uri);
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  _reportData = (): ReportRow[] => {
    const reportHistory = filter(
      this.props.reports[this.state.selectedReportType],
      (date) => includes(date, this.state.selectedYearMonth)
    );
    return map(
      orderBy(reportHistory, (date) => date, "desc"),
      (date) => {
        return {
          date,
        };
      }
    );
  };

  _reportColumns = (): Array<IconTableColumn<ReportRow>> => {
    const columns: Array<IconTableColumn<ReportRow>> = [
      {
        label: "Report Date",
        key: "date",
        customComponent: (props) => {
          const commonDateFormat = "MMM Do YYYY";
          return <> {moment(props.date).format(commonDateFormat)}</>;
        },
      },
      {
        customComponent: (props) => {
          return (
            <span style={{ textAlign: "center", width: 100 }}>
              <Button
                btnLabel="Download CSV"
                name="action"
                color="action"
                size="sm"
                type="button"
                onClick={() => {
                  this._downloadCSV(props.date);
                }}
              />
            </span>
          );
        },
      },
    ];
    return columns;
  };

  render() {
    if (this.props.isFetching) {
      return <IconSpinner centered />;
    }
    const selectedReportTypeHasData = !isEmpty(
      this.props.reports[this.state.selectedReportType]
    );

    const columns = this._reportColumns();
    const data = this._reportData();

    const historyLabel = startCase(this.state.selectedReportType);

    return (
      <div id="admin-reports">
        <h2 style={{ color: "#0a2540" }}>Compliance Reports</h2>
        <Card>
          <Card.Body>
            <Row>
              <Form.Group as={Col} md={6}>
                <IconHeader
                  variant="labelHeader"
                  headerText="Select Report Type"
                />
                <Form.Select
                  // @ts-expect-error react-bootstrap Form.Select has no `sm` prop; pre-existing inert prop forwarded to underlying <select>, preserved verbatim
                  sm={4}
                  name="reportType"
                  value={this.state.selectedReportType}
                  onChange={(e) =>
                    this.setState({
                      selectedReportType: e.target.value,
                      selectedYearMonth: "",
                    })
                  }
                >
                  <option value="" disabled>
                    Select Report Type
                  </option>
                  {map(this.props.reports, (item, reportType) => (
                    <option value={reportType} key={reportType}>
                      {reportType}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>
            {this.state.selectedReportType && selectedReportTypeHasData && (
              <Row className="mt-3">
                <Form.Group as={Col} md={6}>
                  <IconHeader
                    variant="labelHeader"
                    headerText="Select Report Month"
                  />
                  <Form.Select
                    // @ts-expect-error react-bootstrap Form.Select has no `sm` prop; pre-existing inert prop forwarded to underlying <select>, preserved verbatim
                    sm={4}
                    name="selectedYearMonth"
                    value={this.state.selectedYearMonth}
                    onChange={(e) =>
                      this.setState({ selectedYearMonth: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Select Report Month
                    </option>
                    {buildDateSelectOptions(
                      this.props.reports[this.state.selectedReportType]
                    )}
                  </Form.Select>
                </Form.Group>
              </Row>
            )}
            {!selectedReportTypeHasData &&
              this.state.selectedReportType !== "" && (
                <Row>
                  <Col>
                    <IconMessageCard
                      header="Empty Report"
                      subheader="It looks like we have not generated any reports for the above
                report type"
                      icon={
                        <FiFolder color="white" stroke="#60A4BF" size={16} />
                      }
                    />
                  </Col>
                </Row>
              )}
            {this.state.selectedReportType && this.state.selectedYearMonth && (
              <Row>
                <Col>
                  <IconTableHeader
                    tableHeader={historyLabel}
                    tableCountLabel="Reports"
                    tableCount={size(this.state.selectedReportType)}
                  />

                  <IconTable columns={columns} data={data} />
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default connector(withApollo<Props>(AdminReports));
